import { AnimatePresence, motion } from 'motion/react';

interface WorkingIndicatorProps {
  visible: boolean;
  label?: string;
}

/**
 * The travelling-highlight treatment every "still going" label shares, so a
 * caller that draws its own live label (e.g. a chat turn folding one into a
 * button) animates identically to {@link WorkingIndicator}.
 */
export const SHIMMER_TEXT_CLASS =
  'bg-gradient-to-r from-muted-foreground/40 via-muted-foreground/70 to-muted-foreground/40 bg-clip-text text-transparent bg-[length:200%_100%] animate-[text-shimmer_2s_linear_infinite] motion-reduce:animate-none';

/**
 * The "the agent is still going" row, shown while we wait for its next
 * output. It says `Working…` rather than `Thinking…` because it covers the
 * whole turn — tool calls included — and a live `thinking` block draws its own
 * `Thinking…` shimmer for the narrower case of extended reasoning.
 *
 * Renders only the label — callers that need an accompanying avatar (e.g.
 * {@link AssistantTurn}) draw their own outside this component, so the
 * indicator never duplicates the agent icon the surrounding turn already
 * shows.
 */
export function WorkingIndicator({ visible, label = 'Working…' }: WorkingIndicatorProps) {
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
          className="flex min-h-6 items-center py-1"
          data-testid="working-indicator"
        >
          <span className={`text-xs ${SHIMMER_TEXT_CLASS}`}>{label}</span>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
